/**
 * Tipi TypeScript per il sistema di segnalazione dei compro oro
 */

// Tipi di segnalazione disponibili
export type ReportType = 
  | 'CLOSED_PERMANENTLY'  // Attività chiusa definitivamente
  | 'NOT_A_DEALER'        // Non è un compro oro
  | 'WRONG_ADDRESS'       // Indirizzo errato
  | 'OTHER';              // Altro

// Stati delle segnalazioni
export type ReportStatus = 
  | 'PENDING'    // In attesa di revisione
  | 'REVIEWED'   // Revisionata
  | 'RESOLVED'   // Risolta
  | 'REJECTED';  // Rifiutata

// Interfaccia base per una segnalazione
export interface DealerReport {
  id: string;
  dealer_id: string;
  user_id: string | null;
  report_type: ReportType;
  description: string | null;
  status: ReportStatus;
  admin_notes: string | null;
  admin_user_id: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

// Interfaccia estesa con dettagli del dealer e utente
export interface DealerReportWithDetails extends DealerReport {
  dealer_name: string;
  dealer_address: string;
  dealer_city: string | null;
  dealer_phone: string | null;
  dealer_active: boolean;
  user_email: string | null;
  user_full_name: string | null;
  admin_email: string | null;
  admin_full_name: string | null;
}

// Dati per creare una nuova segnalazione
export interface CreateDealerReportData {
  dealer_id: string;
  report_type: ReportType;
  description?: string;
}

// Dati per aggiornare una segnalazione (admin)
export interface UpdateDealerReportData {
  status?: ReportStatus;
  admin_notes?: string;
  admin_user_id?: string;
  resolved_at?: string | null;
}

// Filtri per la ricerca delle segnalazioni
export interface DealerReportFilters {
  status?: ReportStatus | ReportStatus[];
  report_type?: ReportType | ReportType[];
  dealer_id?: string;
  user_id?: string;
  admin_user_id?: string;
  date_from?: string;
  date_to?: string;
  search?: string; // Ricerca nel nome dealer o descrizione
}

// Opzioni di ordinamento
export interface DealerReportSortOptions {
  field: 'created_at' | 'updated_at' | 'dealer_name' | 'status' | 'report_type';
  direction: 'asc' | 'desc';
}

// Risposta paginata per le segnalazioni
export interface DealerReportsResponse {
  data: DealerReportWithDetails[];
  count: number;
  page: number;
  limit: number;
  total_pages: number;
}

// Statistiche delle segnalazioni per admin
export interface DealerReportStats {
  total: number;
  pending: number;
  reviewed: number;
  resolved: number;
  rejected: number;
  by_type: {
    [K in ReportType]: number;
  };
  recent_count: number; // Segnalazioni degli ultimi 7 giorni
}

// Azioni disponibili per l'admin
export type AdminReportAction = 
  | 'approve'           // Approva la segnalazione
  | 'reject'            // Rifiuta la segnalazione
  | 'resolve'           // Risolvi la segnalazione
  | 'deactivate_dealer' // Disattiva il compro oro
  | 'edit_dealer'       // Modifica i dati del compro oro
  | 'delete_dealer';    // Elimina il compro oro

// Dati per le azioni admin
export interface AdminActionData {
  action: AdminReportAction;
  notes?: string;
  dealer_updates?: {
    active?: boolean;
    name?: string;
    address?: string;
    phone?: string;
    city?: string;
  };
}

// Etichette user-friendly per i tipi di segnalazione
export const REPORT_TYPE_LABELS: Record<ReportType, string> = {
  CLOSED_PERMANENTLY: 'Attività chiusa definitivamente',
  NOT_A_DEALER: 'Non è un compro oro',
  WRONG_ADDRESS: 'Indirizzo errato',
  OTHER: 'Altro'
};

// Etichette user-friendly per gli stati
export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  PENDING: 'In attesa',
  REVIEWED: 'Revisionata',
  RESOLVED: 'Risolta',
  REJECTED: 'Rifiutata'
};

// Colori per gli stati (per UI)
export const REPORT_STATUS_COLORS: Record<ReportStatus, string> = {
  PENDING: 'yellow',
  REVIEWED: 'blue',
  RESOLVED: 'green',
  REJECTED: 'red'
};

// Icone per i tipi di segnalazione
export const REPORT_TYPE_ICONS: Record<ReportType, string> = {
  CLOSED_PERMANENTLY: 'store-x',
  NOT_A_DEALER: 'alert-triangle',
  WRONG_ADDRESS: 'map-pin-off',
  OTHER: 'help-circle'
};

// Validazione per i tipi di segnalazione
export const isValidReportType = (type: string): type is ReportType => {
  return ['CLOSED_PERMANENTLY', 'NOT_A_DEALER', 'WRONG_ADDRESS', 'OTHER'].includes(type);
};

// Validazione per gli stati
export const isValidReportStatus = (status: string): status is ReportStatus => {
  return ['PENDING', 'REVIEWED', 'RESOLVED', 'REJECTED'].includes(status);
};

// Utility per ottenere la priorità di una segnalazione
export const getReportPriority = (report: DealerReport): 'high' | 'medium' | 'low' => {
  if (report.report_type === 'CLOSED_PERMANENTLY') return 'high';
  if (report.report_type === 'NOT_A_DEALER') return 'high';
  if (report.report_type === 'WRONG_ADDRESS') return 'medium';
  return 'low';
};

// Utility per calcolare i giorni dalla creazione
export const getDaysFromCreation = (createdAt: string): number => {
  const created = new Date(createdAt);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - created.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};
